// Bulletproof `getServerTime` + `onReady` wrapper for TradingView Advanced
// Charts.
//
// Symptom this fixes: on the MCX_GOLD 1m chart, the next-candle countdown
// starts INCREASING (00:46 → 01:46 → 05:09) and the corner clock freezes
// at a fixed time. The countdown is `barCloseTime − now` — if `now`
// (TradingView's internal server clock) freezes, each new candle pushes
// barCloseTime forward one interval while `now` stays put, and the
// displayed remainder grows by 60 s every minute.
//
// Two silent failure modes freeze `now`:
//
//   1. `onReady` returns a config missing `supports_time: true` — TV core
//      then NEVER calls `getServerTime` at all. Nothing ticks.
//
//   2. `onReady` says supports_time=true and TV calls `getServerTime`, but
//      the stock UDFCompatibleDatafeed's implementation silently swallows
//      fetch errors (its catch block never invokes the callback). One
//      Cloudflare hiccup or a NaN or a millisecond-vs-second mistake and
//      the callback never fires. TV holds the last known `now` forever.
//
// This wrapper defeats BOTH failure modes:
//
//   - It overrides `onReady` to force `supports_time: true` into the
//     config, guaranteeing TV core calls `getServerTime` on its usual
//     cadence no matter what the backend returned.
//   - It overrides `getServerTime` to always invoke the callback on every
//     path — including every failure path — with UNIX SECONDS from a
//     locally-ticking clock that piggybacks a server skew resync every
//     5 min. NaN is guarded. Any skew larger than 5 min is discarded as
//     a unit / timezone mistake.
//   - `window.__tradelog_gst_counter` increments on every callback so the
//     fix can be verified from DevTools without production access.

const RESYNC_MS = 5 * 60 * 1000;   // resync from backend every 5 min
const MAX_SKEW_SEC = 5 * 60;       // reject any skew larger than 5 min

export function wrapDatafeedWithServerTime(target, timeUrl) {
  if (!target) return target;

  // Skew (server - client) in seconds. 0 = trust the browser clock.
  let skewSec = 0;
  let lastSyncMs = 0;

  async function resync() {
    lastSyncMs = Date.now();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      const r = await fetch(timeUrl, { credentials: "same-origin", signal: controller.signal });
      clearTimeout(timeout);
      if (!r.ok) return;
      const raw = await r.text();
      const parsed = parseInt(String(raw).trim(), 10);
      if (!Number.isFinite(parsed) || parsed < 1_500_000_000 || parsed > 4_000_000_000) return;
      const nowSec = Math.floor(Date.now() / 1000);
      const measured = parsed - nowSec;
      if (Math.abs(measured) <= MAX_SKEW_SEC) skewSec = measured;
    } catch (_) {
      // Silent — the local clock keeps ticking. That's the whole point.
    }
  }

  // Sync once at startup; don't await — the widget can start on the browser
  // clock and correct itself as soon as the sync lands.
  resync();

  const clockCb = (callback) => {
    try {
      if (Date.now() - lastSyncMs > RESYNC_MS) resync();
      const secs = Math.floor(Date.now() / 1000) + skewSec;
      const out = Number.isFinite(secs) ? secs : Math.floor(Date.now() / 1000);
      if (typeof window !== "undefined") {
        window.__tradelog_gst_counter = (window.__tradelog_gst_counter || 0) + 1;
        window.__tradelog_gst_last = out;
      }
      callback(out);
    } catch (_) {
      try { callback(Math.floor(Date.now() / 1000)); } catch (__) {}
    }
  };

  return new Proxy(target, {
    get(t, prop, receiver) {
      if (prop === "getServerTime") {
        return function getServerTime(callback) { clockCb(callback); };
      }
      // Force `supports_time: true` into the config TV core reads at
      // startup. Without this, TV never even asks for getServerTime and
      // the clock silently stops ticking under some backend configs.
      if (prop === "onReady") {
        return function onReady(cb) {
          const wrapped = (config) => {
            const safe = Object.assign({}, config || {}, { supports_time: true });
            try { cb(safe); } catch (_) {}
          };
          try { return t.onReady(wrapped); } catch (_) { wrapped({}); }
        };
      }
      const v = Reflect.get(t, prop, receiver);
      return typeof v === "function" ? v.bind(t) : v;
    },
  });
}
